/**
 * studentLiveAdapter — le live de l'élève, du compte et de la salle → ce que ses pages montrent.
 *
 * Les sources d'aujourd'hui :
 *  - **la séance** : `currentUser.live_sessions[liveSessionId]` ; son en-tête par l'adaptateur de
 *    l'entrée (`toLiveEntry`, la prochaine occurrence, le tuteur, les matières) ;
 *  - **le tuteur et moi** : le roster de la séance (`users`, `is_tutor`, `avatar`) et la salle (qui
 *    est connecté, caméra, micro) ; le tuteur de la scène est le principal du moteur
 *    (`liveTutorCast` : le remplaçant du jour, sinon le tuteur de la séance), présent quand un de
 *    ses appareils est dans la salle ; les autres tuteurs connectés, en petits ronds ;
 *  - **les autres élèves** : leur nombre seul, ceux que la salle voit connectés, moi compris
 *    (Wajih, 20/10 : l'en-tête le dit, leurs visages ne sont plus sur la scène) — jamais leur prénom
 *    ni leur caméra ;
 *  - **le chat** : une puce par tuteur du roster et « Tous » dans un cours partagé (`chatChannels`
 *    du moteur), chacune avec ses non-lus (`unread_private_messages_count` par interlocuteur,
 *    `unread_messages_count` pour le groupe) ; la pastille de la barre compte ceux de ces puces ;
 *  - **les activités** : `todo_activities`, sans celles que le tuteur a retirées (`isDeleted`) ;
 *    l'activité à ouvrir quand rien ne l'est : la suggestion du starting block de cette séance,
 *    sinon la plus récente (`ActivityWrapperScreen`).
 */
import moment from "moment"

import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { getMostRecentActivity } from "../../../../../data/utility/dataUtils"
import { chatChannels, chatUnreadTotal } from "../../../../../engine/live/student/liveChatChannels"
import { type LiveTutorCast } from "../../../../../engine/live/student/liveTutorCast"
import { type UILiveChatChannelOption, type UILiveParticipant } from "../../../ui/types/studentLive"
import { toUITutor } from "../../parent/LiveSessions/liveSessionListAdapter"
import { type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { toLiveEntry, type ApiEntryAccount } from "../LiveEntry/liveEntryAdapter"

export type ApiLiveRosterMember = {
    id?: number | string
    is_tutor?: boolean
    display_name?: string | null
    avatar?: string | null
}

export type ApiLiveSessionForStudent = {
    id?: number | string
    users?: ApiLiveRosterMember[] | null
    tutor?: ApiLiveRosterMember | null
    thread_id?: string | number | null
    thread_ids?: Record<string, string | number | null | undefined> | null
    unread_private_messages_count?: Record<string, number | null | undefined> | null
    unread_messages_count?: number | null
} & Record<string, unknown>

export type ApiLiveAccount = ApiEntryAccount & {
    id?: number | string
    firstname?: string | null
    display_name?: string | null
    avatar?: string | null
    live_sessions?: (ApiLiveSessionForStudent & NonNullable<ApiEntryAccount["live_sessions"]>[number])[] | null
    todo_activities?: ApiLiveTodo[] | null
    activities?: ApiLiveTodo[] | null
}

export type ApiLiveTodo = { id?: number; isDeleted?: boolean } & Record<string, unknown>

/** Ce que la salle dit d'un participant : connecté, sa caméra, son micro. */
export type LiveRoomMember = { id: string; isLocal: boolean; cameraOn: boolean; micOn: boolean }

/** L'adresse d'un avatar servi par l'API, ou rien quand il n'y en a pas. */
export const avatarOf = (path: string | null | undefined): string | undefined =>
    path != null && path.trim() !== "" ? cleanMediaUrl(path) : undefined

export function findLiveSession(
    account: ApiLiveAccount | null | undefined,
    liveSessionId: number | string | null
): ApiLiveSessionForStudent | null {
    if (liveSessionId == null) return null
    return account?.live_sessions?.find((session) => String(session?.id) === String(liveSessionId)) ?? null
}

/**
 * L'en-tête du live : l'heure, la durée, le tuteur, les matières de la séance. Le tuteur nommé est le
 * principal (`principal`, le remplaçant du jour sinon celui de la séance) : celui de la scène.
 */
export function toLiveHeading(args: {
    account: ApiLiveAccount | null | undefined
    liveSessionId: number | string | null
    subjects: ApiSchoolSubject[] | null | undefined
    principal?: ApiLiveRosterMember | null
    now?: moment.Moment
}) {
    const entry = toLiveEntry({
        account: args.account,
        liveSessionId: args.liveSessionId ?? "",
        apiSubjects: args.subjects,
        liveParticipants: [],
        now: args.now,
    })
    if (entry.session == null || args.principal == null) return entry.session
    return { ...entry.session, tutor: toUILiveTutor(args.principal) }
}

/** La capsule d'un tuteur de la séance, pour l'en-tête et le chat. */
export function toUILiveTutor(member: ApiLiveRosterMember | null | undefined) {
    return toUITutor({
        id: member?.id != null ? Number(member.id) : undefined,
        display_name: member?.display_name ?? undefined,
        avatar: avatarOf(member?.avatar),
    })
}

/**
 * Un tuteur de la séance, par son id : sa ligne du roster, ou le tuteur de la séance (`tutor`) quand
 * le roster ne le porte pas.
 */
function tutorMember(session: ApiLiveSessionForStudent | null, id: string | null): ApiLiveRosterMember | null {
    if (id == null) return null
    const fromRoster = (session?.users ?? []).find((member) => member?.id != null && String(member.id) === id)
    if (fromRoster != null) return fromRoster
    return session?.tutor?.id != null && String(session.tutor.id) === id ? session.tutor : null
}

/**
 * Le tuteur sur la scène : le principal (`liveTutorCast`, le remplaçant du jour sinon celui de la
 * séance), son nom, sa photo, et sa caméra et son micro tels que la salle les voit — sur tous ses
 * appareils : sa caméra allumée si l'un filme, son micro coupé seulement si tous le sont.
 */
export function toLiveTutor(
    session: ApiLiveSessionForStudent | null,
    cast: LiveTutorCast
): { tutor: UILiveParticipant; present: boolean } {
    const member = tutorMember(session, cast.principalId)
    const inRoom = cast.principal
    return {
        tutor: {
            id: cast.principalId ?? "tutor",
            name: member?.display_name ?? "",
            avatarUrl: avatarOf(member?.avatar),
            tutor: true,
            cameraOff: inRoom == null || !inRoom.cameraOn,
            micOff: inRoom == null || !inRoom.micOn,
        },
        present: inRoom != null,
    }
}

/**
 * Les petits ronds de la scène, dans l'ordre de `liveTutorCast` : les autres caméras du principal
 * quand il filme depuis plusieurs appareils (à son nom), puis les autres tuteurs connectés, leur
 * caméra ou leur photo. L'id d'un rond est sa clé sur la scène, celle de sa piste (`round:<id>`).
 */
export function toLiveTutorRounds(session: ApiLiveSessionForStudent | null, cast: LiveTutorCast): UILiveParticipant[] {
    return cast.rounds.map((round) => {
        const member = tutorMember(session, round.personId)
        return {
            id: round.key,
            name: member?.display_name ?? "",
            avatarUrl: avatarOf(member?.avatar),
            tutor: true,
            cameraOff: !round.cameraOn,
            micOff: !round.micOn,
        }
    })
}

/** Le principal pour l'en-tête et le chat : sa capsule, son nom. */
export function principalTutorOf(
    session: ApiLiveSessionForStudent | null,
    cast: LiveTutorCast
): ApiLiveRosterMember | null {
    return tutorMember(session, cast.principalId)
}

/**
 * Moi, sur ma vignette : la tuile écrit « Toi ». Mon nom ne sert qu'à mon visage sans photo : la lettre
 * de mon prénom (Wajih, 20/10 : « partout où un élève n'a pas d'avatar, la lettre de son prénom »), pas
 * les deux initiales de « Prénom N. ».
 */
export function toLiveMe(
    account: ApiLiveAccount | null | undefined,
    flags: { cameraOn: boolean; micOn: boolean; handRaised: boolean }
): UILiveParticipant {
    return {
        id: String(account?.id ?? "me"),
        name: firstnameOf(account),
        avatarUrl: avatarOf(account?.avatar),
        cameraOff: !flags.cameraOn,
        micOff: !flags.micOn,
        handRaised: flags.handRaised,
    }
}

/** Le prénom d'un compte : `firstname`, sinon le premier mot de `display_name` (« Prénom N. »). */
function firstnameOf(account: ApiLiveAccount | null | undefined): string {
    const firstname = account?.firstname?.trim()
    if (firstname != null && firstname !== "") return firstname
    return (account?.display_name ?? "").trim().split(/\s+/)[0] ?? ""
}

/**
 * Les élèves connectés au live, moi compris : les élèves du roster que la salle voit, sans le tuteur.
 * Leur nombre seul : ni prénom, ni visage.
 */
export function liveConnectedStudents(
    session: ApiLiveSessionForStudent | null,
    members: LiveRoomMember[],
    selfId: number | string | null | undefined
): number {
    const connected = new Set(members.filter((member) => !member.isLocal).map((member) => member.id))
    const others = (session?.users ?? []).filter(
        (member) =>
            member?.id != null &&
            member.is_tutor !== true &&
            String(member.id) !== String(selfId) &&
            connected.has(String(member.id))
    )
    return others.length + 1
}

/**
 * La pastille du chat : les non-lus de tous les fils que ses puces montrent (`chatUnreadTotal`). Un
 * fil que le relevé compte mais qu'aucune puce n'ouvre n'allume plus la pastille.
 */
export function liveUnreadCount(session: ApiLiveSessionForStudent | null): number {
    return chatUnreadTotal(session)
}

/**
 * Les puces du chat : une par tuteur du roster — son nom, sa capsule, le tuteur de la séance en
 * premier — et « Tous » dans un cours partagé, chacune avec les non-lus de son fil. Les mêmes que les
 * vignettes de l'ancien chat (`chatMembers` = les tuteurs du roster), et que le chat lit pour choisir
 * le fil qui s'ouvre (`chatChannelToOpen`).
 */
export function liveChatChannelOptions(session: ApiLiveSessionForStudent | null): UILiveChatChannelOption[] {
    const byId = new Map((session?.users ?? []).map((member) => [String(member?.id), member]))
    return chatChannels(session).map((entry) => {
        if (entry.tutorId == null) return { id: entry.channel, unread: entry.unread }
        const member = byId.get(entry.tutorId) ?? (String(session?.tutor?.id) === entry.tutorId ? session?.tutor : null)
        return {
            id: entry.channel,
            unread: entry.unread,
            tutor: toUITutor({
                id: Number(entry.tutorId),
                display_name: member?.display_name ?? undefined,
                avatar: avatarOf(member?.avatar),
            }),
        }
    })
}

/**
 * L'activité ouverte, à jour. Le relevé de l'activité (sa copie, sa correction) ne se range que dans
 * l'activité courante du store (`data.currentActivity`) : c'est elle qu'on lit dès qu'elle est la
 * bonne. La liste à faire, puis l'activité poussée par le tuteur (une activité finie qu'il affiche
 * n'est pas dans la liste), ne servent qu'avant le premier relevé — l'activité poussée ne donne que
 * son identité, sa copie y resterait figée.
 */
export function liveOpenActivity<T extends { id?: number | string | null }>(args: {
    activityId: number | null
    stored: T | null | undefined
    todos: T[]
    pushed: T | null | undefined
}): T | null {
    const { activityId, stored, todos, pushed } = args
    if (activityId == null) return null
    if (stored != null && stored.id === activityId) return stored
    const fromList = todos.find((todo) => todo.id === activityId)
    if (fromList != null) return fromList
    return pushed != null && pushed.id === activityId ? pushed : null
}

/** Les activités à faire, sans celles que le tuteur vient de retirer. */
export function liveTodos(account: ApiLiveAccount | null | undefined): ApiLiveTodo[] {
    return (account?.todo_activities ?? []).filter((todo) => todo?.id != null && todo.isDeleted !== true)
}

/** Où les listes de l'élève rangent une activité : à faire, faite, ou nulle part. */
export type LiveActivityPlace = "todo" | "done" | "gone"

/**
 * Où est l'activité ouverte ? Le serveur range chacune des activités de l'élève à faire
 * (`todo_activities`) ou faite (`activities`, celles des 7 derniers jours, 15 au plus) ; le tuteur ne
 * retire qu'une activité à faire. `gone` : absente des deux. `null` : le compte ne porte pas ses listes,
 * rien ne se juge.
 */
export function liveActivityPlace(
    account: ApiLiveAccount | null | undefined,
    activityId: number
): LiveActivityPlace | null {
    if (!Array.isArray(account?.todo_activities) || !Array.isArray(account?.activities)) return null
    if (liveTodos(account).some((todo) => todo.id === activityId)) return "todo"
    return account.activities.some((activity) => activity?.id === activityId) ? "done" : "gone"
}

/**
 * L'activité à ouvrir quand rien ne l'est : la suggestion de Sam pour cette séance si elle est dans
 * la liste, sinon la plus récente des activités à faire. `null` : plus rien à faire.
 *
 * Écart voulu : l'ancien écran prenait aussi la plus récente des activités FINIES (son écran de
 * résultat) ; le live montre « Tu n'as plus d'activités ».
 */
export function pickLiveActivity(todos: ApiLiveTodo[], suggestedId: number | null | undefined): number | null {
    if (suggestedId != null) {
        const suggested = todos.find((todo) => todo.id === suggestedId)
        if (suggested?.id != null) return suggested.id
    }
    const recent = getMostRecentActivity(todos, null) as ApiLiveTodo | null
    return recent?.id ?? null
}
